import * as React from 'react';
import { PopoverContent, PopoverTrigger, Popover as Root } from '../design-system/popover';

export type PopoverProps = {
  trigger: React.ReactNode;
  content?: () => React.ReactNode;
  side?: React.ComponentProps<typeof PopoverContent>['side'];
  sideOffset?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export default function Popover({
  trigger,
  content,
  side = 'right',
  sideOffset = 0,
  open,
  onOpenChange,
}: PopoverProps) {
  let renderedContent: React.ReactNode = null;

  const [internalOpen, setInternalOpen] = React.useState<boolean>(false);

  const controlledOpen = open !== undefined ? open : internalOpen;

  if (controlledOpen && content) renderedContent = content();

  const handleOpenChange = (next: boolean) => {
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  let triggerNode = trigger;
  if (React.isValidElement(trigger)) {
    const trg = trigger as React.ReactElement<React.HTMLAttributes<HTMLElement>>;
    const existingOnClick = trg.props.onClick as React.MouseEventHandler | undefined;
    const existingRole = trg.props.role as string | undefined;
    const existingTabIndex = trg.props.tabIndex;

    const newProps: Partial<React.HTMLAttributes<HTMLElement>> = {
      onClick: (e: React.MouseEvent) => {
        if (existingOnClick) existingOnClick(e);
        const next = !controlledOpen;
        if (open === undefined) setInternalOpen(next);
        onOpenChange?.(next);
      },
      role: existingRole ?? 'button',
      tabIndex: existingTabIndex ?? 0,
      'aria-expanded': controlledOpen,
      'aria-haspopup': 'dialog',
    };
    triggerNode = React.cloneElement(trg, newProps);
  }

  return (
    <Root open={controlledOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerNode}</PopoverTrigger>
      <PopoverContent
        side={side}
        sideOffset={sideOffset}
        align="start"
        className="rounded-sm p-4 border-[1px] border-muted"
      >
        {renderedContent}
      </PopoverContent>
    </Root>
  );
}
